import React from 'react';

import {StickySidebarContentPanel} from 'app/components/general/DetailPage/RightSidebar/structure/StickySidebarContentPanel';
import {StickySidebarMenuButton} from 'app/components/general/DetailPage/RightSidebar/structure/StickySidebarMenuButton';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {LoadableBaseDiagram} from 'app/procedures/components/ProcedureDiagram/BaseDiagram';
import {PrintDiagramButton} from 'app/procedures/components/ProcedureDiagram/PrintDiagramButton';
import {
  diagramDesktopLayoutOptions,
  diagramFullscreenLayoutOptions,
  diagramFullscreenPaperOptions,
  diagramGeneralOptions,
  diagramPaperOptions,
} from 'app/procedures/components/ProcedureDiagram/ProcedureDiagramSidebar';
import {removeOrphanChoices} from 'app/procedures/utils';
import {processOrVersionToContent} from 'app/process/content-converters';
import {DIAGRAM_WIDGET} from 'app/settings/slice';
import {scrollIntoStep} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';

import type {
  StickyContentPanelType,
  StickyMenuType,
  StickySidebarElementType,
} from 'app/components/general/DetailPage/RightSidebar/structure/types';
import type {
  AnyStepType,
  ProcedureHashidSerializedType,
  ProcessHashidSerializedType,
} from 'sweet-types';

const StickyDiagramContentPanel = <
  T extends ProcessHashidSerializedType | ProcedureHashidSerializedType
>({
  object,
  version,
  setFullscreenState,
  isFullscreen = false,
  stepsWithoutOrphanChoices,
}: StickyContentPanelType<T> & {
  stepsWithoutOrphanChoices: Array<AnyStepType>;
}) => {
  return (
    <StickySidebarContentPanel
      object={object}
      setFullscreenState={setFullscreenState}
      isFullscreen={isFullscreen}
      topActions={
        <>
          <LazyTooltip tooltip={gt('Print diagram to image')}>
            <PrintDiagramButton
              versionId={version.id}
              versionContentType={version.content_type}
            />
          </LazyTooltip>
        </>
      }
      widgetName={DIAGRAM_WIDGET}
    >
      <LoadableBaseDiagram
        elements={'view'}
        onStepClick={scrollIntoStep}
        layoutOptions={
          isFullscreen
            ? diagramFullscreenLayoutOptions
            : diagramDesktopLayoutOptions
        }
        paperOptions={
          isFullscreen ? diagramFullscreenPaperOptions : diagramPaperOptions
        }
        generalOptions={isFullscreen ? undefined : diagramGeneralOptions}
        steps={stepsWithoutOrphanChoices}
      >
        {(diagram, zoomControl) => (
          <div
            className={
              isFullscreen ? 'diagram-canvas flex-grow-1 position-relative' : ''
            }
          >
            {zoomControl}
            {diagram}
          </div>
        )}
      </LoadableBaseDiagram>
    </StickySidebarContentPanel>
  );
};

const StickyDiagramContentPanelForProcesses = ({
  object,
  version,
  setFullscreenState,
  isFullscreen = false,
}: StickyContentPanelType<ProcessHashidSerializedType>) => {
  return (
    <StickyDiagramContentPanel
      object={object}
      version={version}
      setFullscreenState={setFullscreenState}
      isFullscreen={isFullscreen}
      stepsWithoutOrphanChoices={removeOrphanChoices(
        processOrVersionToContent(version, object).content
      )}
    />
  );
};

const StickyDiagramContentPanelForProcedures = ({
  object,
  version,
  setFullscreenState,
  isFullscreen = false,
}: StickyContentPanelType<ProcedureHashidSerializedType>) => {
  return (
    <StickyDiagramContentPanel
      object={object}
      version={version}
      setFullscreenState={setFullscreenState}
      isFullscreen={isFullscreen}
      stepsWithoutOrphanChoices={removeOrphanChoices(version.content)}
    />
  );
};

const StickyDiagramMenuButton = <
  ItemType extends ProcessHashidSerializedType | ProcedureHashidSerializedType
>({
  object,
  buttonDelta,
}: StickyMenuType<ItemType>) => {
  return (
    <StickySidebarMenuButton
      object={object}
      buttonDelta={buttonDelta}
      icon="fa-sitemap"
      widgetName={DIAGRAM_WIDGET}
    />
  );
};

export const StickyDiagramForProcesses: StickySidebarElementType<ProcessHashidSerializedType> =
  {
    Panel: StickyDiagramContentPanelForProcesses,
    MenuButton: StickyDiagramMenuButton,
    type: 'right',
    sidebarName: DIAGRAM_WIDGET,
  };

export const StickyDiagramForProcedures: StickySidebarElementType<ProcedureHashidSerializedType> =
  {
    Panel: StickyDiagramContentPanelForProcedures,
    MenuButton: StickyDiagramMenuButton,
    type: 'right',
    sidebarName: DIAGRAM_WIDGET,
  };
